import { useClientSide } from '@carvana/merch-react-hooks';
import dynamic from 'next/dynamic';
import { memo, useContext } from 'react';
import useLoadSaveSearchNudge from '@/srp/Modals/SaveSearchNudge/hooks/useLoadSaveSearchNudge';
import {
  useDeviceType,
  useIsMobile,
  useIsNative
} from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import { useHasSoftPull } from '~/context/srp/FinanceContext';
import ModalContext from '~/context/srp/ModalContext';
import useShowTradeIncentives from '~/hooks/common/selectors/useShowTradeIncentives';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

const ActivationModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ActivationModal" */ '@/srp/Modals/ActivationModal'
    ),
  { ssr: false }
);

const FilterModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "FilterModal" */ '@/srp/Modals/FilterModal/FilterModalWrapper'
    ),
  { ssr: false }
);

const GeicoDiscountModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "GeicoDiscountModal" */ '@/common/Modals/GeicoDiscountModal'
    ),
  { ssr: false }
);

const GeolocationModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "GeolocationModal" */ '@/srp/Modals/GeolocationModal'
    ),
  { ssr: false }
);

const GreatDealModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "GreatDealModal" */ '@/srp/Modals/GreatDealModal'
    ),
  { ssr: false }
);

const MoreDetailsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "MoreDetailsModal" */ '@/srp/Modals/MoreDetailsModal'
    ),
  { ssr: false }
);

const ReviewModal = dynamic(
  () =>
    import(/* webpackChunkName: "ReviewModal" */ '@/srp/Modals/ReviewModal'),
  { ssr: false }
);

const RootDiscountModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "RootDiscountModal" */ '@/common/Modals/RootDiscountModal'
    ),
  { ssr: false }
);

const SaveSearchModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SaveSearchModal" */ '@/srp/Modals/SaveSearchModal/SaveSearchModal'
    ),
  { ssr: false }
);

const SaveSearchNudge = dynamic(
  () =>
    import(
      /* webpackChunkName: "SaveSearchNudge" */ '@/srp/Modals/SaveSearchNudge'
    ),
  { ssr: false }
);

const SoftPullModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SoftPullModal" */ '@/srp/Modals/SoftPullModal'
    ),
  { ssr: false }
);

const SortByModal = dynamic(
  () =>
    import(/* webpackChunkName: "SortByModal" */ '@/srp/Modals/SortByModal'),
  { ssr: false }
);

const AppraisalRefreshModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "AppraisalRefreshModal" */ '@/srp/Modals/AppraisalRefreshModal'
    ),
  { ssr: false }
);

const SampleTermsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SampleTermsModal" */ '@/common/helios/Modals/SampleTermsModal'
    ),
  { ssr: false }
);

const ShippingModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ShippingModal" */ '@/common/helios/Modals/ShippingModal'
    ),
  { ssr: false }
);

const TradeIncentiveModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "TradeIncentiveModal" */ '@/common/Modals/TradeIncentiveModal'
    ),
  { ssr: false }
);

const WIPNotificationsModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "WIPNotificationsModal" */ '@/srp/Modals/WIPNotifications'
    )
);

type OwnProps = {
  loadActivationModal: boolean;
  loadAppraisalRefreshModal: boolean;
  loadAuthedModals: boolean;
  loadFilterModal: boolean;
  loadGeolocationModal: boolean;
  loadSampleTermsModal: boolean;
  loadSaveSearchNudge: boolean;
  loadSoftPullModal: boolean;
  loadSortByModal: boolean;
  loadTradeIncentiveModal: boolean;
};

const ModalsContent = memo(
  ({
    loadActivationModal,
    loadAppraisalRefreshModal,
    loadAuthedModals,
    loadFilterModal,
    loadGeolocationModal,
    loadSampleTermsModal,
    loadSaveSearchNudge,
    loadSoftPullModal,
    loadSortByModal,
    loadTradeIncentiveModal
  }: OwnProps) => {
    const {
      geicoDiscount,
      isGeicoDiscountModalOpen,
      isRootDiscountModalOpen,
      isSampleTermsModalOpen,
      isShippingModalOpen,
      isTradeIncentiveModalOpen,
      rootDiscount,
      setGeicoDiscountModalOpen,
      setRootDiscountModalOpen,
      setSampleTermsModalOpen,
      setShippingModalOpen,
      setIsTradeIncentiveModalOpen
    } = useContext(ModalContext);

    return (
      <>
        {loadActivationModal && <ActivationModal />}
        {loadAuthedModals && (
          <>
            <SaveSearchModal />
            <WIPNotificationsModal />
          </>
        )}
        {loadFilterModal && <FilterModal />}
        <GeicoDiscountModal
          discount={geicoDiscount}
          isOpen={isGeicoDiscountModalOpen}
          setGeicoDiscountModalOpen={setGeicoDiscountModalOpen}
        />
        {rootDiscount && (
          <RootDiscountModal
            discount={rootDiscount}
            isOpen={isRootDiscountModalOpen}
            setRootDiscountModalOpen={setRootDiscountModalOpen}
          />
        )}
        {loadGeolocationModal && <GeolocationModal />}
        <GreatDealModal />
        <MoreDetailsModal />
        <ReviewModal />
        {loadSaveSearchNudge && <SaveSearchNudge />}
        {loadAppraisalRefreshModal && <AppraisalRefreshModal />}
        <ShippingModal
          isShippingModalOpen={isShippingModalOpen}
          setShippingModalOpen={setShippingModalOpen}
        />
        {loadSampleTermsModal && (
          <SampleTermsModal
            isSampleTermsModalOpen={isSampleTermsModalOpen}
            setSampleTermsModalOpen={setSampleTermsModalOpen}
          />
        )}
        {loadSoftPullModal && <SoftPullModal />}
        {loadSortByModal && <SortByModal />}
        {loadTradeIncentiveModal && (
          <TradeIncentiveModal
            isTradeIncentiveModalOpen={isTradeIncentiveModalOpen}
            setIsTradeIncentiveModalOpen={setIsTradeIncentiveModalOpen}
          />
        )}
      </>
    );
  }
);

function ModalsState() {
  const isLoggedIn = useIsLoggedIn();
  const isClientSide = useClientSide();
  const isDealership = useIsDealershipPage();
  const isNative = useIsNative();
  const isMobile = useIsMobile();
  const hasValidSoftPull = useHasSoftPull();
  const { isDesktop } = useDeviceType();
  const loadSaveSearchNudge = useLoadSaveSearchNudge();
  const showTradeIncentive = useShowTradeIncentives();

  //TODO determine what the check should be for activation modal
  return (
    <ModalsContent
      loadActivationModal={!isDealership}
      loadAppraisalRefreshModal={!isDealership && !isNative}
      loadAuthedModals={isLoggedIn}
      loadFilterModal={!isDesktop}
      loadGeolocationModal={isClientSide && !isDealership}
      loadSampleTermsModal={!hasValidSoftPull}
      loadSaveSearchNudge={loadSaveSearchNudge}
      loadSoftPullModal={!isDealership && !isNative}
      loadSortByModal={isMobile}
      loadTradeIncentiveModal={showTradeIncentive}
    />
  );
}

export default withErrorBoundary(ModalsState, { section: 'SRP Modals' });
